import { useEffect, useRef } from 'react';
import { useRichTextEditor } from './RichTextEditorProvider';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { Box } from '@mui/material';
import { sharedContentStyles } from './richTextEditorStyles';
import { INSERT_UNORDERED_LIST_COMMAND } from '@lexical/list';
import { KEY_DOWN_COMMAND, COMMAND_PRIORITY_NORMAL } from 'lexical';
import type { SerializedEditorState } from 'lexical';
import { Placeholder } from './Placeholder';
import { colors } from '@sweep-io/sweep-design';
import useResizeObserver from 'use-resize-observer';

const LexicalEditor = ({
  children,
  placeholder,
  hideOutline,
  onHeightChange,
  editable = true,
}: {
  children?: React.ReactNode;
  placeholder?: string;
  hideOutline?: boolean;
  onHeightChange?: (height: number) => void;
  editable?: boolean;
}) => {
  const contentEditableRef = useRef<HTMLDivElement>(null);

  useResizeObserver({
    ref: contentEditableRef,
    onResize: ({ height }) => {
      if (height !== undefined) {
        onHeightChange?.(height);
      }
    },
  });

  return (
    <Box className="editor-container" sx={{ '&.editor-container': sharedContentStyles }}>
      <RichTextPlugin
        contentEditable={
          <ContentEditable
            className="editor-content"
            ref={contentEditableRef}
            style={{ outline: hideOutline ? 'none' : '1px solid ' + colors.blue[500] }}
            contentEditable={editable}
          />
        }
        placeholder={<Placeholder placeholder={placeholder} />}
        ErrorBoundary={LexicalErrorBoundary}
      />
      <ListPlugin />
      <LinkPlugin attributes={{ target: '_blank', rel: 'noopener noreferrer' }} />
      {children}
    </Box>
  );
};

// Editor Component - Renders the actual editor with toolbar
export const RichTextEditor = ({
  onChange,
  onBlur,
  initialContent,
  placeholder,
  autoFocus,
  hideOutline,
  onHeightChange,
  editable = true,
}: {
  onChange?: (serializedState: string) => void;
  onBlur?: () => void;
  initialContent?: string; // Serialized Lexical editor state as JSON string
  placeholder?: string;
  autoFocus?: boolean;
  hideOutline?: boolean;
  onHeightChange?: (height: number) => void;
  editable?: boolean;
}) => {
  const { editor } = useRichTextEditor();
  const isInitialized = useRef(false);
  const currentContentRef = useRef<string>(initialContent || '');

  // Register keyboard shortcut for bullet list (Ctrl+Shift+8 or Cmd+Shift+8)
  useEffect(() => {
    return editor.registerCommand(
      KEY_DOWN_COMMAND,
      (event: KeyboardEvent) => {
        if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key === '8') {
          event.preventDefault();
          editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
          return true;
        }
        return false;
      },
      COMMAND_PRIORITY_NORMAL,
    );
  }, [editor]);

  // Set initial content only on mount and focus the editor
  useEffect(() => {
    if (initialContent && !isInitialized.current) {
      isInitialized.current = true;
      try {
        // Parse the serialized editor state JSON string
        const serializedState: SerializedEditorState = JSON.parse(initialContent);
        // Parse and set the editor state
        const editorState = editor.parseEditorState(serializedState);
        editor.setEditorState(editorState);
      } catch (error) {
        console.error('Error parsing initial content:', error);
        // If parsing fails, initialize with empty state
        editor.setEditorState(
          editor.parseEditorState({
            root: {
              children: [],
              direction: 'ltr',
              format: '',
              indent: 0,
              type: 'root',
              version: 1,
            },
          }),
        );
      }
    }

    // Register blur event handler
    if (onBlur) {
      return editor.registerRootListener((rootElement, prevRootElement) => {
        if (prevRootElement) {
          prevRootElement.removeEventListener('blur', onBlur);
        }
        if (rootElement) {
          rootElement.addEventListener('blur', onBlur);
        }
      });
    }
  }, [editor, onBlur, initialContent]);

  useEffect(() => {
    if (autoFocus && editable) {
      // Use queueMicrotask to defer focus until after React has updated the DOM
      queueMicrotask(() => {
        editor.focus();
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editable]);

  // When editor state changes, propagate serialized state
  useEffect(() => {
    const unsubscribe = editor.registerUpdateListener(({ editorState }: any) => {
      try {
        // Serialize the editor state to JSON
        const serializedState = editorState.toJSON();
        const serializedString = JSON.stringify(serializedState);

        // Store current content in ref for blur handler
        currentContentRef.current = serializedString;

        // Only call onChange for real-time updates
        // onBlur will be handled by the blur event listener above
        if (serializedString !== initialContent) {
          onChange?.(serializedString);
        }
      } catch (error) {
        console.error('Error serializing editor content:', error);
      }
    });
    return unsubscribe;
  }, [editor, onChange, initialContent]);

  // Update editor editable state when prop changes
  useEffect(() => {
    editor.setEditable(editable);
  }, [editor, editable]);

  return (
    <LexicalEditor
      placeholder={placeholder}
      hideOutline={hideOutline}
      onHeightChange={onHeightChange}
      editable={editable}
    />
  );
};
